import React from 'react';

import { calculatePercentage, getOccupancyBandColor } from '@app/containers/components/OccupancyRate';
import DevText from '@components/Text';

import { BarDivider, BarFill, BarList, BarRowButton, BarTrack } from '../../styled';

export interface OccupancyBarItem {
    id: string;
    title: string;
    score: number;
}

export interface OccupancyBarsProps {
    overallScore: number | null;
    items: OccupancyBarItem[];
    selectedId: string | null;

    // Called with the clicked item ID, or null for Overall Capacity
    onSelect?: (id: string | null) => void;
}

// Horizontal occupancy bars; lengths share one scale so a team over 100% visibly overshoots the rest
const OccupancyBars: React.FC<OccupancyBarsProps> = ({ overallScore, items, selectedId, onSelect }) => {
    const scale = Math.max(1, overallScore ?? 0, ...items.map((item) => item.score));

    const renderRow = (id: string | null, title: string, score: number | null, emphasized = false) => {
        const isSelected = id !== null && id === selectedId;
        const color = score === null ? undefined : getOccupancyBandColor(score);
        const select = () => onSelect && onSelect(id);

        return (
            <BarRowButton
                key={id ?? 'overall'}
                role={onSelect ? 'button' : undefined}
                tabIndex={onSelect ? 0 : undefined}
                aria-pressed={onSelect && id !== null ? isSelected : undefined}
                title={
                    onSelect ?
                        isSelected || id === null ?
                            'Show all members'
                        :   `Show ${title} members`
                    :   undefined
                }
                $selected={isSelected}
                $emphasized={emphasized}
                onClick={select}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        select();
                    }
                }}
            >
                <span>{title}</span>
                <BarTrack>
                    {score === null ?
                        <DevText inline muted>
                            No occupancy yet
                        </DevText>
                    :   <>
                            <BarFill $width={(score / scale) * 85} $color={color as string} $selected={isSelected} />
                            <DevText inline color={color}>
                                {calculatePercentage(score)}%
                            </DevText>
                        </>
                    }
                </BarTrack>
            </BarRowButton>
        );
    };

    return (
        <BarList>
            {renderRow(null, 'Overall Capacity', overallScore, true)}
            <BarDivider />
            {items.map((item) => renderRow(item.id, item.title, item.score))}
        </BarList>
    );
};

export default OccupancyBars;
